/** Session-scoped screen-share mask for CRM financials. */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  applySanitizeFinanceDocumentFlag,
  readSanitizeFinanceSession,
  setFinanceSanitizedFlag,
  startFinanceSanitizeObserver,
  writeSanitizeFinanceSession,
} from '../utils/sanitizeFinance';
import '../styles/sanitizeFinance.css';

export interface SanitizeFinanceContextValue {
  enabled: boolean;
  toggle: () => void;
  setEnabled: (next: boolean) => void;
}

const SanitizeFinanceContext = createContext<SanitizeFinanceContextValue | undefined>(undefined);

function syncSanitizeFlag(enabled: boolean): void {
  setFinanceSanitizedFlag(enabled);
  applySanitizeFinanceDocumentFlag(enabled);
  writeSanitizeFinanceSession(enabled);
}

export const SanitizeFinanceProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [enabled, setEnabledState] = useState(() => {
    const on = readSanitizeFinanceSession();
    syncSanitizeFlag(on);
    return on;
  });

  useEffect(() => {
    syncSanitizeFlag(enabled);
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return undefined;
    return startFinanceSanitizeObserver();
  }, [enabled]);

  const setEnabled = useCallback((next: boolean) => {
    setEnabledState(next);
  }, []);

  const toggle = useCallback(() => {
    setEnabledState(current => !current);
  }, []);

  const value = useMemo(
    () => ({ enabled, toggle, setEnabled }),
    [enabled, toggle, setEnabled],
  );

  return (
    <SanitizeFinanceContext.Provider value={value}>
      {children}
    </SanitizeFinanceContext.Provider>
  );
};

export function useSanitizeFinance(): SanitizeFinanceContextValue {
  const ctx = useContext(SanitizeFinanceContext);
  if (!ctx) {
    throw new Error('useSanitizeFinance must be used within SanitizeFinanceProvider');
  }
  return ctx;
}

